<script>
import LabelDropdownItem from './LabelDropdownItem.vue';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { picoSearch } from '@scmmishra/pico-search';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { funilLabelTitle } from 'dashboard/api/aceleres/funilLabels';

export default {
  components: {
    LabelDropdownItem,
    AddLabelModal,
    Hotkey,
    NextButton,
  },

  props: {
    accountLabels: {
      type: Array,
      default: () => [],
    },
    selectedLabels: {
      type: Array,
      default: () => [],
    },
    allowCreation: {
      type: Boolean,
      default: false,
    },
    /**
     * Aceleres: cria a etiqueta pelo funil em vez de abrir o modal de
     * configuracao do Chatwoot. So o nome e pedido -- a cor vem da etapa do
     * funil da empresa, e e o executor quem a escolhe.
     */
    funilCreation: {
      type: Boolean,
      default: false,
    },
    /**
     * Titulos que podem ser APAGADOS de vez — os que esta funcionalidade
     * criou. Chega pronto do pai; o dropdown nao decide a regra.
     */
    deletableTitles: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update', 'add', 'remove', 'create', 'delete'],

  data() {
    return {
      search: '',
      createModalVisible: false,
      labelsBeforeCreate: [],
    };
  },

  computed: {
    createLabelPlaceholder() {
      const label = this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
      return this.search ? `${label}:` : label;
    },

    filteredActiveLabels() {
      if (!this.search) return this.accountLabels;

      return picoSearch(this.accountLabels, this.search, ['title']);
    },

    noResult() {
      return this.filteredActiveLabels.length === 0;
    },

    hasExactMatchInResults() {
      return this.filteredActiveLabels.some(
        label => label.title === this.search
      );
    },

    shouldShowCreate() {
      // Aceleres: no fluxo do funil a opcao de criar fica sempre visivel --
      // "Disponibilizar a opcao Criar nova etiqueta" nao pode depender de a
      // busca ter devolvido menos de 3 resultados, que era a regra do fluxo
      // antigo (o de abrir o modal de configuracao).
      if (this.funilCreation) return this.allowCreation;

      return this.allowCreation && this.filteredActiveLabels.length < 3;
    },

    parsedSearch() {
      return this.funilCreation
        ? funilLabelTitle(this.search)
        : sanitizeLabel(this.search);
    },

    /** Ja existe etiqueta com o nome que a pessoa digitou. */
    funilTitleTaken() {
      return this.accountLabels.some(
        label => label.title === this.parsedSearch
      );
    },
  },

  watch: {
    accountLabels: {
      deep: true,
      handler(labels) {
        if (!this.createModalVisible) return;

        const previousTitles = new Set(this.labelsBeforeCreate);
        const createdLabel = labels.find(
          label =>
            !previousTitles.has(label.title) &&
            label.title === this.parsedSearch
        );

        if (createdLabel) {
          this.onAdd(createdLabel);
          this.hideCreateModal();
        }
      },
    },
  },

  mounted() {
    this.focusInput();
  },

  methods: {
    focusInput() {
      this.$refs.searchbar?.focus({ preventScroll: true });
    },

    clearSearch() {
      this.search = '';
      this.$nextTick(() => this.focusInput());
    },

    updateLabels(label) {
      this.$emit('update', label);
    },

    onAdd(label) {
      this.$emit('add', label);
    },

    onRemove(label) {
      this.$emit('remove', label);
    },

    onAddRemove(label) {
      if (this.selectedLabels.includes(label.title)) {
        this.onRemove(label.title);
      } else {
        this.onAdd(label);
      }
    },

    showCreateModal() {
      this.labelsBeforeCreate = this.accountLabels.map(label => label.title);
      this.createModalVisible = true;
    },

    hideCreateModal() {
      this.createModalVisible = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-h-[12.5rem]">
    <!-- Aceleres 2026-08-19: o titulo saiu daqui. Ele dizia "Adicionar
         etiquetas", exatamente o que o campo e o botao ao lado ja dizem — o
         mesmo texto duas vezes na mesma caixa. Fica so a tecla de atalho, a
         direita. -->
    <div class="flex items-center justify-end mb-1">
      <Hotkey
        custom-class="border border-solid text-n-slate-12 bg-n-slate-2 text-xxs border-n-strong flex-shrink-0"
      >
        {{ 'L' }}
      </Hotkey>
    </div>
    <div class="relative flex-auto flex-grow-0 flex-shrink-0 mb-2 max-h-8">
      <input
        ref="searchbar"
        v-model="search"
        type="text"
        class="search-input ltr:pr-8 rtl:pl-8"
        :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
      />
      <button
        v-if="search"
        type="button"
        class="absolute top-1/2 ltr:right-1.5 rtl:left-1.5 flex size-5 -translate-y-1/2 items-center justify-center rounded text-n-slate-9 transition-colors hover:text-n-slate-12 focus-visible:ring-2 focus-visible:ring-n-brand"
        :aria-label="$t('INBOX.CLEAR_SEARCH')"
        @click="clearSearch"
      >
        <span class="i-lucide-x size-3.5" aria-hidden="true" />
      </button>
    </div>
    <div
      class="flex items-start justify-start flex-auto flex-grow flex-shrink overflow-auto"
    >
      <div class="w-full my-1">
        <woot-dropdown-menu>
          <LabelDropdownItem
            v-for="label in filteredActiveLabels"
            :key="label.title"
            :title="label.title"
            :color="label.color"
            :label-id="label.id"
            :selected="selectedLabels.includes(label.title)"
            :deletable="funilCreation && deletableTitles.includes(label.title)"
            @select-label="onAddRemove(label)"
            @delete-label="$emit('delete', $event)"
          />
        </woot-dropdown-menu>
        <div
          v-if="noResult"
          class="flex justify-center py-4 px-2.5 font-medium text-xs text-n-slate-11"
        >
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
        </div>
        <!-- Aceleres: criar etiqueta pelo funil. Sem modal e sem escolher
             cor -- so o nome. A cor sai da etapa do funil da empresa. -->
        <div
          v-if="funilCreation && shouldShowCreate"
          class="flex flex-col gap-1 pt-1 border-t border-solid border-n-weak"
        >
          <NextButton
            icon="i-lucide-plus"
            slate
            sm
            ghost
            class="justify-start"
            :label="`${createLabelPlaceholder} ${parsedSearch}`"
            @click="$emit('create', search)"
          />
          <span
            v-if="funilTitleTaken"
            class="px-2 pb-1 text-xs text-n-slate-11"
          >
            {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.ALREADY_EXISTS') }}
          </span>
          <span v-else class="px-2 pb-1 text-xs text-n-slate-11">
            {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.FUNIL_HINT') }}
          </span>
        </div>
        <div
          v-else-if="allowCreation && shouldShowCreate"
          class="flex pt-1 border-t border-solid border-n-weak"
        >
          <NextButton
            icon="i-lucide-plus"
            slate
            sm
            ghost
            :label="`${createLabelPlaceholder} ${parsedSearch}`"
            :disabled="hasExactMatchInResults"
            @click="showCreateModal"
          />

          <woot-modal
            v-model:show="createModalVisible"
            :on-close="hideCreateModal"
          >
            <AddLabelModal
              :prefill-title="parsedSearch"
              @close="hideCreateModal"
            />
          </woot-modal>
        </div>
      </div>
    </div>
  </div>
</template>
